import { Button } from "@/components/Button/Button";
import { CloseSvg } from "@/components/Icon/Icon";
import { POSTHOG_VISIBLE } from "@/constants/posthog";
import { Color } from "@/typed/Common";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, SxProps, Theme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

interface DialogProps<T> {
  title: string;
  message: React.ReactNode;
  args: T;
  onClose: (approve: boolean, args: T) => void;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  hideConfirm?: boolean;
  hideDecline?: boolean;
  hideClose?: boolean;
  confirmText?: string;
  confirmColor?: Color;
  confirmDisable?: boolean;
  declineText?: string;
  declineColor?: Color;
  ignoreClosed?: boolean;
  sx?: SxProps<Theme>;
}

export const DialogBox = <T,>(props: DialogProps<T>) => {
  const { t } = useTranslation();
  const handleClose = (result: boolean | object) => {
    props.setOpen(false);
    if (typeof result !== "boolean" && props.ignoreClosed) {
      return;
    }
    props.onClose(result === true, props.args);
  };

  return (
    <Dialog
      open={props.open}
      onClose={!props.hideClose ? handleClose : () => undefined}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{ ...props.sx }}
    >
      <Box component="div" sx={{ display: "flex", width: "100%", alignItems: "center" }}>
        <DialogTitle id="alert-dialog-title">{props.title}</DialogTitle>
        <Box
          component="div"
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "flex-end",
            alignItems: "center",
            paddingRight: "16px"
          }}
        >
          {!props.hideClose && (
            <IconButton id="alert-dialog-close" onClick={(event: React.SyntheticEvent) => handleClose(event)} sx={{ height: "40px" }}>
              <CloseSvg color="inherit" />
            </IconButton>
          )}
        </Box>
      </Box>
      <Divider />
      <DialogContent>{props.message}</DialogContent>
      {(!props.hideConfirm || !props.hideDecline) && (
        <>
          <Divider />
          <DialogActions sx={{ padding: "1em", gap: "0.5em" }}>
            {!props.hideDecline && (
              <Button id="dialog-decline" color={props.declineColor ?? "secondary"} onClick={() => handleClose(false)} {...POSTHOG_VISIBLE}>
                {props.declineText ?? t("common:dialog.no")}
              </Button>
            )}
            {!props.hideConfirm && (
              <Button
                id="dialog-confirm"
                color={props.confirmColor ?? "primary"}
                disabled={props.confirmDisable}
                onClick={() => handleClose(true)}
                autoFocus
                {...POSTHOG_VISIBLE}
              >
                {props.confirmText ?? t("common:dialog.yes")}
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
